import * as types from '../types/authTypes';
// This would be imported from your services folder in a real app
import authService from '../../services/authService';
import api from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';

// Action creators
export const loginRequest = () => ({
  type: types.AUTH_LOGIN_REQUEST,
});

export const loginSuccess = (user, accessToken, refreshToken, pendingInvitations = []) => ({
  type: types.AUTH_LOGIN_SUCCESS,
  payload: { user, accessToken, refreshToken, pendingInvitations },
});

export const loginFailure = (error) => ({
  type: types.AUTH_LOGIN_FAILURE,
  payload: error,
});

export const logout = () => ({
  type: types.AUTH_LOGOUT,
});

export const registerRequest = () => ({
  type: types.AUTH_REGISTER_REQUEST,
});

export const registerSuccess = (user) => ({
  type: types.AUTH_REGISTER_SUCCESS,
  payload: user,
});

export const registerFailure = (error) => ({
  type: types.AUTH_REGISTER_FAILURE,
  payload: error,
});

export const resetPasswordRequest = () => ({
  type: types.AUTH_RESET_PASSWORD_REQUEST,
});

export const resetPasswordSuccess = () => ({
  type: types.AUTH_RESET_PASSWORD_SUCCESS,
});

export const resetPasswordFailure = (error) => ({
  type: types.AUTH_RESET_PASSWORD_FAILURE,
  payload: error,
});
export const refreshSuccess = (accessToken) => ({
  type: types.AUTH_REFRESH_SUCCESS,
  payload: { accessToken },
});

export const setUserDetails = (user) => ({
  type: types.SET_USER_DETAILS,
  payload: user,
});

export const removePendingInvitation = (membershipId) => {
  // Update sessionStorage
  const stored = JSON.parse(sessionStorage.getItem('pendingInvitations') || '[]');
  const updated = stored.filter(inv => inv.membershipId !== membershipId);
  sessionStorage.setItem('pendingInvitations', JSON.stringify(updated));
  
  return {
    type: types.REMOVE_PENDING_INVITATION,
    payload: membershipId,
  };
};
export const login = (credentials) => async (dispatch) => {
  dispatch(loginRequest());
  try {
    const response = await authService.login(credentials);
    const data = response.data || response;
    
    // Check if account is banned (403)
    if (response && response.status === 403) {
      window.location.href = '/banned';
      return;
    }
    
    // Check if account is on hold (202)
    if (response && response.status === 202) {
      window.location.href = '/waiting';
      return;
    }
    
    if (data && data.user && data.accessToken && data.refreshToken) {
      // Store pendingInvitations in sessionStorage for persistence across rerenders
      if (data.pendingInvitations && data.pendingInvitations.length > 0) {
        sessionStorage.setItem('pendingInvitations', JSON.stringify(data.pendingInvitations));
      }
      dispatch(loginSuccess(data.user, data.accessToken, data.refreshToken, data.pendingInvitations || []));
      return data;
    }
    
    dispatch(loginFailure('Try again, user login failed'));
    throw new Error('Try again, user login failed');
  } catch (error) {
    let errorMessage = 'Login failed';
    let requiresOtpVerification = false;
    let userEmail = null;
    let isBanned = false;
    let isOnHold = false;

    if (error.response) {
      // Check for device limit reached (409)
      if (error.response.status === 409 && error.response.data?.message === 'DEVICE_LIMIT_REACHED') {
        dispatch({
          type: types.AUTH_DEVICE_LIMIT_REACHED,
          payload: {
            activeSessions: error.response.data.activeSessions || [],
            maxDevices: error.response.data.maxDevices || 5,
          },
        });
        const deviceError = new Error('DEVICE_LIMIT_REACHED');
        deviceError.isDeviceLimitReached = true;
        deviceError.activeSessions = error.response.data.activeSessions || [];
        deviceError.maxDevices = error.response.data.maxDevices || 5;
        throw deviceError;
      }

      // Check for banned account (403)
      if (error.response.status === 403 && error.response.data?.message?.toLowerCase().includes('banned')) {
        isBanned = true;
        window.location.href = '/banned';
        return;
      }
      
      // Check for hold account (202)
      if (error.response.status === 202) {
        isOnHold = true;
        window.location.href = '/waiting';
        return;
      }
      
      if (error.response.status === 403 && error.response.data?.requiresOtpVerification) {
        requiresOtpVerification = true;
        userEmail = error.response.data.email;
        errorMessage = error.response.data.message;
      } else if (error.response.status === 401 || error.response.status === 403) {
        errorMessage = error.response.data?.message || 'Invalid credentials';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    dispatch(loginFailure(errorMessage));
    const errorData = new Error(errorMessage);
    errorData.requiresOtpVerification = requiresOtpVerification;
    errorData.email = userEmail;
    errorData.isBanned = isBanned;
    errorData.isOnHold = isOnHold;
    throw errorData;
  }
};

export const refreshToken = () => async (dispatch) => {
  try {
    const newAccessToken = await authService.refreshToken();
    dispatch(refreshSuccess(newAccessToken));
    return newAccessToken;
  } catch (error) {
    dispatch(logout());
    throw error;
  }
};

export const resetPassword = (token, newPassword) => async (dispatch) => {
  dispatch(resetPasswordRequest());
  try {
    const response = await api.put(`${API_ENDPOINTS.AUTH.RESET_PASSWORD}/${token}`, {
      password: newPassword,
    });
    dispatch(resetPasswordSuccess());
    return { success: true, message: response.message || 'Password reset successful' };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
    dispatch(resetPasswordFailure(errorMessage));
    throw error;
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await authService.logout();
    dispatch(logout());
  } catch (error) {
    console.error('Logout error:', error);
    await authService.logout();
    dispatch(logout());
  }
};

export const register = (userData) => async (dispatch) => {
  dispatch(registerRequest());
  try {
    const response = await authService.register(userData);
    const data = response.data || response;
    if (data && data.user) {
      dispatch(registerSuccess(data.user));
      if (data.accessToken && data.refreshToken) {
        dispatch(loginSuccess(data.user, data.accessToken, data.refreshToken));
      }
      return data.user;
    } else if (data && data.success) {
      dispatch(registerSuccess(null));
      return data.message || 'Registration successful! Please check your email for OTP.';
    }
    dispatch(registerFailure('Registration successful, but user data was not received.'));
    throw new Error('Registration successful, but user data was not received.');
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
    dispatch(registerFailure(errorMessage));
    throw error;
  }
};

export const checkUserSession = () => async (dispatch) => {
  const refreshToken = authService.getRefreshToken();
  let accessToken = authService.getTokenFromCookie();

  if (!refreshToken) {
    dispatch(logout());
    return;
  }

  if (refreshToken && !accessToken) {
    try {
      const newAccessToken = await authService.refreshToken();
      accessToken = newAccessToken;
    } catch (refreshError) {
      dispatch(logoutUser());
      return;
    }
  }

  if (authService.isAuthenticated() && accessToken) {
    try {
      const user = await authService.getUserDetails(); // New function to fetch user details
      
      // Restore pendingInvitations from sessionStorage if available
      const storedInvitations = JSON.parse(sessionStorage.getItem('pendingInvitations') || '[]');
      
      dispatch(setUserDetails(user)); // Dispatch to Redux
      dispatch(loginSuccess(user, accessToken, refreshToken, storedInvitations)); // Include pending invitations
    } catch (error) {
      console.error("Failed to fetch user details or session invalid, logging out:", error);
      dispatch(logoutUser());
    }
  } else {
    dispatch(logoutUser());
  }
};




export const requestLoginOtpRequest = () => ({ type: types.AUTH_REQUEST_LOGIN_OTP_REQUEST });
export const requestLoginOtpSuccess = (message) => ({ type: types.AUTH_REQUEST_LOGIN_OTP_SUCCESS, payload: message });
export const requestLoginOtpFailure = (error) => ({ type: types.AUTH_REQUEST_LOGIN_OTP_FAILURE, payload: error });

export const requestLoginOtp = (email) => async (dispatch) => {
  dispatch(requestLoginOtpRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.REQUEST_LOGIN_OTP, { email });
    dispatch(requestLoginOtpSuccess(response.message || 'OTP sent to your email.'));
    return response.message;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to request OTP';
    dispatch(requestLoginOtpFailure(errorMessage));
    throw error;
  }
};

export const verifyLoginOtpRequest = () => ({ type: types.AUTH_VERIFY_LOGIN_OTP_REQUEST });
export const verifyLoginOtpSuccess = (user, accessToken, refreshToken) => ({ type: types.AUTH_VERIFY_LOGIN_OTP_SUCCESS, payload: { user, accessToken, refreshToken } });
export const verifyLoginOtpFailure = (error) => ({ type: types.AUTH_VERIFY_LOGIN_OTP_FAILURE, payload: error });

export const verifyLoginOtpAndLogin = (email, otp) => async (dispatch) => {
  dispatch(verifyLoginOtpRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_LOGIN_OTP, { email, otp });
    
    // Check if account is banned (403)
    if (response && response.status === 403) {
      window.location.href = '/banned';
      return;
    }
    
    // Check if account is on hold (202)
    if (response && response.status === 202) {
      window.location.href = '/waiting';
      return;
    }
    
    if (response && response.user && response.accessToken && response.refreshToken) {
      authService.setTokens(response.accessToken, response.refreshToken);
      dispatch(verifyLoginOtpSuccess(response.user, response.accessToken, response.refreshToken));
      return response;
    }
    dispatch(verifyLoginOtpFailure('OTP verified, but user or tokens missing.'));
    throw new Error('Try again, user login failed');
  } catch (error) {
    // Check for device limit reached (409)
    if (error.response?.status === 409 && error.response?.data?.message === 'DEVICE_LIMIT_REACHED') {
      dispatch({
        type: types.AUTH_DEVICE_LIMIT_REACHED,
        payload: {
          activeSessions: error.response.data.activeSessions || [],
          maxDevices: error.response.data.maxDevices || 5,
        },
      });
      const deviceError = new Error('DEVICE_LIMIT_REACHED');
      deviceError.isDeviceLimitReached = true;
      deviceError.activeSessions = error.response.data.activeSessions || [];
      deviceError.maxDevices = error.response.data.maxDevices || 5;
      throw deviceError;
    }

    // Check for banned or on-hold status in error response
    if (error.response?.status === 403 && error.response?.data?.message?.toLowerCase().includes('banned')) {
      window.location.href = '/banned';
      return;
    }
    if (error.response?.status === 202) {
      window.location.href = '/waiting';
      return;
    }
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to verify OTP and login';
    dispatch(verifyLoginOtpFailure(errorMessage));
    throw error;
  }
};


export const forgotPasswordRequest = () => ({ type: types.AUTH_FORGOT_PASSWORD_REQUEST });
export const forgotPasswordSuccess = (message) => ({ type: types.AUTH_FORGOT_PASSWORD_SUCCESS, payload: message });
export const forgotPasswordFailure = (error) => ({ type: types.AUTH_FORGOT_PASSWORD_FAILURE, payload: error });

export const forgotPassword = (email) => async (dispatch) => {
  dispatch(forgotPasswordRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    const successMessage = response.message || 'Password reset OTP sent to your email.';
    dispatch(forgotPasswordSuccess(successMessage));
    return { success: true, message: successMessage };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send reset OTP';
    dispatch(forgotPasswordFailure(errorMessage));
    return { success: false, message: errorMessage };
  }
};

export const verifyPasswordResetOtpRequest = () => ({ type: types.AUTH_VERIFY_PASSWORD_RESET_OTP_REQUEST });
export const verifyPasswordResetOtpSuccess = (message) => ({ type: types.AUTH_VERIFY_PASSWORD_RESET_OTP_SUCCESS, payload: message });
export const verifyPasswordResetOtpFailure = (error) => ({ type: types.AUTH_VERIFY_PASSWORD_RESET_OTP_FAILURE, payload: error });

export const verifyPasswordResetOtp = (email, otp, newPassword) => async (dispatch) => {
  dispatch(verifyPasswordResetOtpRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_PASSWORD_RESET_OTP, { email, otp, newPassword });
    const successMessage = response.message || 'Password has been reset successfully.';
    dispatch(verifyPasswordResetOtpSuccess(successMessage));
    return { success: true, message: successMessage };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to verify OTP and reset password';
    dispatch(verifyPasswordResetOtpFailure(errorMessage));
    throw error;
  }
};

export const verifyRegistrationOtpRequest = () => ({ type: types.AUTH_VERIFY_REGISTRATION_OTP_REQUEST });
export const verifyRegistrationOtpSuccess = (message) => ({ type: types.AUTH_VERIFY_REGISTRATION_OTP_SUCCESS, payload: message });
export const verifyRegistrationOtpFailure = (error) => ({ type: types.AUTH_VERIFY_REGISTRATION_OTP_FAILURE, payload: error });

export const verifyRegistrationOtp = (email, otp) => async (dispatch) => {
  dispatch(verifyRegistrationOtpRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_REGISTRATION, { email, otp });
    dispatch(verifyRegistrationOtpSuccess(response.message || 'Email verified successfully!'));
    return response.message;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to verify registration OTP';
    dispatch(verifyRegistrationOtpFailure(errorMessage));
    throw error;
  }
};

export const checkExistingSessionRequest = () => ({ type: types.AUTH_CHECK_EXISTING_SESSION_REQUEST });
export const checkExistingSessionSuccess = (data) => ({ type: types.AUTH_CHECK_EXISTING_SESSION_SUCCESS, payload: data });
export const checkExistingSessionFailure = (error) => ({ type: types.AUTH_CHECK_EXISTING_SESSION_FAILURE, payload: error });

export const checkExistingSession = (email, password) => async (dispatch) => {
  dispatch(checkExistingSessionRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.CHECK_EXISTING_SESSION, { email, password });

    if (!response) {
      throw new Error('No response received from API');
    }

    dispatch(checkExistingSessionSuccess(response));

    // If device limit is reached, dispatch device limit action
    if (response.isAtDeviceLimit) {
      dispatch({
        type: types.AUTH_DEVICE_LIMIT_REACHED,
        payload: {
          activeSessions: response.activeSessions || [],
          maxDevices: response.maxDevices || 5,
        },
      });
    }

    return response;
  } catch (error) {
    let errorMessage = 'Failed to check existing session';
    let requiresOtpVerification = false;
    let userEmail = null;

    if (error.response) {
      if (error.response.status === 403 && error.response.data?.requiresOtpVerification) {
        requiresOtpVerification = true;
        userEmail = error.response.data.email;
        errorMessage = error.response.data.message;
      } else if (error.response.status === 401 || error.response.status === 403 || error.response.status === 404) {
        errorMessage = error.response.data?.message || 'Invalid credentials';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    dispatch(checkExistingSessionFailure(errorMessage));

    const errorData = new Error(errorMessage);
    errorData.requiresOtpVerification = requiresOtpVerification;
    errorData.email = userEmail;
    throw errorData;
  }
};

export const forceLogoutAllDevicesRequest = () => ({ type: types.AUTH_FORCE_LOGOUT_ALL_DEVICES_REQUEST });
export const forceLogoutAllDevicesSuccess = (message) => ({ type: types.AUTH_FORCE_LOGOUT_ALL_DEVICES_SUCCESS, payload: message });
export const forceLogoutAllDevicesFailure = (error) => ({ type: types.AUTH_FORCE_LOGOUT_ALL_DEVICES_FAILURE, payload: error });

export const forceLogoutAllDevices = (email) => async (dispatch) => {
  dispatch(forceLogoutAllDevicesRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.FORCE_LOGOUT_ALL_DEVICES, { email });
    dispatch(forceLogoutAllDevicesSuccess(response.message));
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to logout from all devices';
    dispatch(forceLogoutAllDevicesFailure(errorMessage));
    throw error;
  }
};

// Logout from specific devices by session IDs
export const logoutSpecificDevicesRequest = () => ({ type: types.AUTH_LOGOUT_SPECIFIC_DEVICES_REQUEST });
export const logoutSpecificDevicesSuccess = (data) => ({ type: types.AUTH_LOGOUT_SPECIFIC_DEVICES_SUCCESS, payload: data });
export const logoutSpecificDevicesFailure = (error) => ({ type: types.AUTH_LOGOUT_SPECIFIC_DEVICES_FAILURE, payload: error });

export const logoutSpecificDevices = (email, sessionIds) => async (dispatch) => {
  dispatch(logoutSpecificDevicesRequest());
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGOUT_SPECIFIC_DEVICES, { email, sessionIds });
    dispatch(logoutSpecificDevicesSuccess(response));
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to logout from selected devices';
    dispatch(logoutSpecificDevicesFailure(errorMessage));
    throw error;
  }
};

export const clearDeviceLimit = () => ({
  type: types.AUTH_CLEAR_DEVICE_LIMIT,
});

export const sessionExpired = () => ({
  type: types.SESSION_EXPIRED,
});

export const resetSessionExpired = () => ({
  type: types.RESET_SESSION_EXPIRED,
});
